import { FUNCTIONS_URL, supabase } from '../lib/supabase';

/**
 * Wrapper for calling Supabase Edge Functions.
 * Adds the current user's JWT and the project's anon key (apikey header).
 */
async function authHeaders(extra = {}) {
  const { data: { session } } = await supabase.auth.getSession();
  const headers = { ...extra };
  if (session?.access_token) {
    headers.Authorization = `Bearer ${session.access_token}`;
  }
  // Edge Functions require the apikey header even when JWT verify is on.
  if (process.env.REACT_APP_SUPABASE_ANON_KEY) {
    headers.apikey = process.env.REACT_APP_SUPABASE_ANON_KEY;
  }
  return headers;
}

async function callJson(path, { method = 'GET', body, params } = {}) {
  let url = `${FUNCTIONS_URL}/${path}`;
  if (params) {
    const qs = new URLSearchParams(params).toString();
    if (qs) url += `?${qs}`;
  }
  const headers = await authHeaders({ 'Content-Type': 'application/json' });
  const res = await fetch(url, {
    method,
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  const text = await res.text();
  const data = text ? safeJson(text) : null;
  if (!res.ok) {
    const message = data?.error || res.statusText || 'Request failed';
    const err = new Error(message);
    err.status = res.status;
    err.response = { status: res.status, data };
    throw err;
  }
  return { data, status: res.status };
}

function safeJson(text) {
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}

// ---- Auth (kept for backwards compatibility with callers; AuthContext is now authoritative) ----
export const authAPI = {
  login: async (email, password) => {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;
    return { data: { access_token: data.session.access_token, user: data.user } };
  },
  register: async (email, password, name) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: { data: { name } },
    });
    if (error) throw error;
    return { data: { access_token: data.session?.access_token, user: data.user } };
  },
  guestLogin: async () => {
    const { data, error } = await supabase.auth.signInAnonymously();
    if (error) throw error;
    return { data: { access_token: data.session.access_token, user: data.user } };
  },
  getMe: async () => {
    const { data } = await supabase.auth.getUser();
    return { data: data.user };
  },
};

// ---- Onboarding ----
export const onboardingAPI = {
  save: (data) => callJson('onboarding', { method: 'POST', body: data }),
  get: () => callJson('onboarding'),
};

// ---- Chat ----
export const chatAPI = {
  sendMessage: (content, sessionId) =>
    callJson('chat', { method: 'POST', body: { content, session_id: sessionId } }),
  getHistory: (sessionId) => callJson(`chat/history/${sessionId}`),
  getSessions: () => callJson('chat/sessions'),
  deleteSession: (sessionId) =>
    callJson(`chat/sessions/${sessionId}`, { method: 'DELETE' }),
};

// ---- TTS (binary response) ----
export const ttsAPI = {
  generateSpeech: async (text) => {
    const headers = await authHeaders({ 'Content-Type': 'application/json' });
    const res = await fetch(`${FUNCTIONS_URL}/tts`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ text }),
    });
    if (!res.ok) throw new Error(`TTS failed: ${res.status}`);
    const blob = await res.blob();
    return URL.createObjectURL(blob);
  },
};

// ---- Mood ----
export const moodAPI = {
  create: (data) => callJson('mood', { method: 'POST', body: data }),
  getAll: (limit = 30) => callJson('mood', { params: { limit } }),
  getStats: (days = 7) => callJson('mood/stats', { params: { days } }),
};

// ---- Journal ----
export const journalAPI = {
  create: (title, content) =>
    callJson('journal', { method: 'POST', body: { title, content } }),
  getAll: (limit = 20) => callJson('journal', { params: { limit } }),
  delete: (id) => callJson(`journal/${id}`, { method: 'DELETE' }),
};

// ---- Session Summaries ----
export const sessionAPI = {
  generateSummary: (sessionId) =>
    callJson(`session-summary/${sessionId}`, { method: 'POST' }),
  getSummaries: () => callJson('session-summary'),
};

// ---- Settings ----
export const settingsAPI = {
  get: () => callJson('settings'),
  update: (settings) => callJson('settings', { method: 'PUT', body: settings }),
  deleteHistory: () => callJson('settings/history', { method: 'DELETE' }),
};

// ---- Voice + ElevenLabs (helpers) ----
export const voiceAPI = {
  /** Send recorded audio, get back AI's spoken reply (blob URL) + metadata in headers. */
  call: async (audioBlob, sessionId) => {
    const headers = await authHeaders();
    const form = new FormData();
    form.append('audio', audioBlob, 'recording.webm');
    if (sessionId) form.append('session_id', sessionId);
    const res = await fetch(`${FUNCTIONS_URL}/voice-call`, {
      method: 'POST',
      headers,
      body: form,
    });
    if (!res.ok) throw new Error(`voice-call failed: ${res.status}`);
    const audio = await res.blob();
    return {
      audioUrl: URL.createObjectURL(audio),
      transcript: decodeURIComponent(res.headers.get('X-Transcript') || ''),
      aiResponse: decodeURIComponent(res.headers.get('X-AI-Response') || ''),
      isCrisis: res.headers.get('X-Is-Crisis') === 'true',
      sessionId: res.headers.get('X-Session-ID') || sessionId,
    };
  },
  transcribe: async (audioBlob) => {
    const headers = await authHeaders();
    const form = new FormData();
    form.append('audio', audioBlob, 'recording.webm');
    const res = await fetch(`${FUNCTIONS_URL}/transcribe`, {
      method: 'POST',
      headers,
      body: form,
    });
    if (!res.ok) throw new Error(`transcribe failed: ${res.status}`);
    return res.json();
  },
};

export const elevenLabsAPI = {
  getSignedUrl: () => callJson('elevenlabs/signed-url'),
  getAgentInfo: () => callJson('elevenlabs/agent-info'),
};
